import React, { useCallback } from 'react';
import styled from 'styled-components/macro';
import { NavLink } from 'react-router-dom';
import { setLastViewedNotification } from '../../redux/modules/siteNotifications';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { FlatPopover } from './styles';
import { useSelector } from 'src/client/redux';
import { useActivityPopoverController } from './helpers/useActivityPopoverController';
import { useSettingsNavigation } from './helpers/useSettingsNavigation';
import { DESKTOP } from 'src/client/constants/mediaQueries';
import { MANAGE_ACCOUNT } from 'src/client/constants/joinCampaigns';

type Props = {
  username: string;
  onRecentsClick: () => void;
  closeNotificationPanel: () => void;
  siteNotificationBadgeCount: number;
  toggleProfileMenu: (next: boolean) => void;
};

export const UserMenu = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    closeNotificationPanel,
    onRecentsClick,
    siteNotificationBadgeCount,
    toggleProfileMenu,
    username,
    ...rest
  } = props;
  const { ssoUrl } = useSelector((state) => state.app);
  const { handleSettingsClick } = useSettingsNavigation();
  const { handleActivityFeedClick } = useActivityPopoverController();

  const dispatch = useDispatch();

  const handleNewsClick = useCallback(() => {
    closeNotificationPanel();
    const timestamp = moment().unix();
    dispatch(setLastViewedNotification({ timestamp }));
  }, [dispatch, closeNotificationPanel]);

  const handleActivityClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleActivityFeedClick();
  };

  const trackedSSOUrl = new URL(ssoUrl);
  trackedSSOUrl.searchParams.set('campaign', MANAGE_ACCOUNT);

  return (
    <>
      <UserPopover ref={ref} {...rest}>
        <Dropdown className="dropdown-menu" onClick={() => toggleProfileMenu(false)}>
          <Header>
            <Avatar className="avatar">{username[0]}</Avatar>
            <UserLabel className="user-label">{username}</UserLabel>
          </Header>
          <Divider />
          <li>
            <a href={trackedSSOUrl.toString()} target="_blank" rel="noopener noreferrer">
              Manage Account
            </a>
          </li>
          <li className="hidden-xs">
            <a id="recents" onClick={onRecentsClick}>
              Recents
            </a>
          </li>
          <li>
            <NavLink to="/favorites">Favorites</NavLink>
          </li>
          <li>
            <NavLink to="/ratings">Ratings</NavLink>
          </li>
          <li>
            <span className="dropdown-item new-releases" onClick={handleNewsClick}>
              News
              {!!siteNotificationBadgeCount && <div className="notification-badge">{siteNotificationBadgeCount}</div>}
            </span>
          </li>
          <ActivityItem>
            <a id="activity" onClick={handleActivityClick}>
              Activity
            </a>
          </ActivityItem>
          <li>
            <NavLink to={{ pathname: '/settings', state: { canGoBack: true } }} onClick={handleSettingsClick}>
              Settings
            </NavLink>
          </li>
          <li>
            <a href="/logout">Logout</a>
          </li>
        </Dropdown>
      </UserPopover>
    </>
  );
});

const UserPopover = styled(FlatPopover)`
  max-width: none;

  .bottom {
    margin-left: -10px;
    margin-top: 22px;

    .arrow {
      margin-left: 20px;
      margin-top: 1px;
    }
  }
`;

const Header = styled.div`
  padding: 3px 20px;
  clear: both;
  font-weight: normal;
  line-height: 1.42857143;
  white-space: nowrap;
  height: 40px;
  margin-top: 5px;
`;

const Avatar = styled.span`
  text-align: center;
  border-radius: 2px;
  width: 36px;
  height: 36px;
  line-height: 36px;
  font-size: 1.15rem;
  display: inline-block;
  vertical-align: middle;
  background: ${(props) => props.theme.primary3};
  color: ${(props) => props.theme.primary4};
`;

const UserLabel = styled.span`
  margin-left: 4px;
  font-size: 1.15rem;
  display: inline-block;
  vertical-align: middle;

  @media (max-width: 767px) {
    width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Divider = styled.li`
  margin: 9px 13px 3px 20px;
  height: 1px;
  overflow: hidden;
`;

const Dropdown = styled.ul`
  list-style: none;
  font-size: 0.9rem;
  padding: 0;
  margin: 0;
  position: static;
  display: block;
  border: 0;
  box-shadow: none;

  li:not(${Divider}) {
    padding: 3px;
    cursor: pointer;
  }

  li > span.new-releases {
    display: block;
    padding: 3px 20px;
    clear: both;
    font-weight: 400;
    line-height: 1.42857143;
    white-space: nowrap;
    display: flex;
    width: 100%;

    > div {
      font-size: 0.75rem;
      border-radius: 4px;
      width: 24px;
      height: 16px;
      background-color: ${(props) => props.theme.headerBarPrimary3};
      text-align: center;
      line-height: 16px;
      font-weight: 700;
      margin-left: 8px;
      color: white;
    }
  }
`;

const ActivityItem = styled.li`
  display: none;

  @media ${DESKTOP} {
    display: list-item;
  }
`;
